/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/jsx-no-bind */
import { compareAsc, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { ThemeProvider } from "styled-components";

import { useMsal } from "@azure/msal-react";
import uuid from "react-uuid";
import FullCalendarApp from "../../components/FullCalendarApp";
import { axiosGetRequest } from "../../api/ApiClient";
import ModalCallback from "../../model/ModalCallback";
import ActivityManagementDateService from "../../service/Docente/ActivityManagementDateService";
import UploadFileStyles from "../../styles/pages/UploadFilesStyles";
import GenericUtils from "../../utils/GenericUtils";
import ModalDetailHoliday from "./ModalDetailHoliday";
import ModalSaveHoliday from "./ModalSaveHoliday";
import DateUtils from "../../utils/DateUtils";
import { useModal } from "../../context/ModalContext";
import GlobalModal from "../../components/GlobalModal";
import { StyledLabel } from "../../styles/MainStyles";
import HolidayParams from "../../model/HolidayParams";

interface IHoliday {
    campus: string;
    color: string;
    description: string;
    end: string;
    id: number;
    start: string;
    title: string;
    type: string;
}

interface ICustomHolidayModal {
    show: boolean;
    type: string;
    readonly: boolean;
}

const initialCustomDetailModal: ICustomHolidayModal = {
    show: false,
    type: "",
    readonly: true,
};

function CalendarView() {
    const { StyledContainerHeader, StyledSubTitle, StyledTitle } =
        UploadFileStyles;
    const theme = {};

    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenDocente();
    const { openModal, isOpen, closeModal } = useModal();

    const [initialDate, setInitialDate] = useState(null);
    const [finalDate, setFinalDate] = useState(null);
    const [year] = useState(new Date().getFullYear());
    const [holidays, setHolidays] = useState<Array<IHoliday>>([]);
    const [openModalSaveHoliday, setOpenModalSaveHoliday] = useState(false);
    const [dateHoliday, setDateHoliday] = useState("");
    const [openModalDetailHoliday, setOpenModalDetailHoliday] = useState(false);
    const [idHoliday, setIdHoliday] = useState("");
    const [openModalDetailCustom, setOpenModalDetailCustom] =
        useState<ICustomHolidayModal>(initialCustomDetailModal);

    const administrationAllowance = "ABONO_ADMINISTRACAO";
    const activityManagementDate = "GESTAO_ATIVIDADES";
    const registrationsAllowedForPastDates = ["18507", "30291"];

    const defaultHolidayParams: HolidayParams = {
        start: dateHoliday,
        date: dateHoliday,
    };

    const getColor = (type: string) => {
        if (type === "N") {
            return "#0B8043";
        }

        if (type === "M") {
            return "#E25E2F";
        }

        if (type === "R") {
            return "#449ADF";
        }

        if (type === activityManagementDate) {
            return "#A16000";
        }

        if (type === administrationAllowance) {
            return "#2F6A99";
        }

        return "";
    };

    async function getData() {
        const newData: any[] = [];

        const [year, month] =
            (initialDate && (initialDate as string).split("-")) || [];
        const currentMonthFinalDate = new Date(Number(year), Number(month), 0)
            .toISOString()
            .split("T")[0];

        const response = await axiosGetRequest(
            `/docente/v1/feriados?data_inicial=${initialDate}&data_final=${currentMonthFinalDate}`,
            acessToken,
            instance
        );
        const abono = await axiosGetRequest(
            `/docente/v1/calendarios?dataRange=${initialDate}...${currentMonthFinalDate}`,
            acessToken,
            instance
        );
        const activityManagementDates =
            await ActivityManagementDateService.getActivityManagementDateByDate(
                `${initialDate}...${currentMonthFinalDate}`,
                instance
            );

        if (GenericUtils.isSuccessStatus(response)) {
            response?.data?.map((data: any) =>
                newData.push({
                    title:
                        data.nomeFeriado +
                        (data.tipoFeriado === "M" || data.tipoFeriado === "R"
                            ? ` - ${data.campus}`
                            : ""),
                    start: data.dataFeriado,
                    end: data.dataFeriado,
                    id: data.calendarioFeriadoId,
                    color: getColor(data.tipoFeriado),
                    type: data.tipoFeriado,
                    description: data.motivoRecesso,
                    campus: data.campus,
                })
            );
        }

        if (GenericUtils.isSuccessStatus(abono)) {
            abono?.data?.calendarios?.map((data: any) =>
                newData.push({
                    title: data.nome,
                    start: data.data,
                    end: data.data,
                    id: Number(data.calendarioId),
                    color: getColor(data.tipo),
                    type: data.tipo,
                    description: data.nome,
                    campus: data.campus.id,
                })
            );
        }

        if (GenericUtils.isSuccessStatus(activityManagementDates)) {
            activityManagementDates?.data?.calendariosGestaoAtividades?.map(
                (data: any) =>
                    newData.push({
                        title: "Gestão de Atividades",
                        start: data.dataSelecionadaNoCalendario,
                        end: data.dataSelecionadaNoCalendario,
                        id: Number(data.calendarioGestaoAtividadesId),
                        color: getColor(activityManagementDate),
                        type: activityManagementDate,
                        description: "",
                        campus: "",
                    })
            );
        }

        setHolidays(newData);
    }

    /**
     * Receber a data como uma string no formato abaixo pois o new Date() retorna um dia a menos devido ao UTC
     * @param dateString 2023-04-21
     * @returns void
     */
    const validateIfDateIsBeforeToday = (date: string) => {
        const [year, month, day] = date.split("-");
        const targetDate = new Date(
            Number(year),
            Number(month) - 1,
            Number(day)
        );
        const today = new Date();

        return DateUtils.isDateBefore(targetDate, today);
    };

    /**
     * Receber a data como uma string no formato abaixo pois o new Date() retorna um dia a menos devido ao UTC
     * @param dateString 2023-04-21
     * @returns void
     */
    const handleOpenModalSaveHoliday = (dateString: string) => {
        if (
            validateIfDateIsBeforeToday(dateString) &&
            !registrationsAllowedForPastDates.includes(
                GenericUtils.getUserAdp()
            )
        ) {
            openModal();
            return;
        }

        setDateHoliday(dateString);
        setOpenModalSaveHoliday(true);
    };

    const handleCloseModalSaveHoliday = (callback?: ModalCallback) => {
        setOpenModalSaveHoliday(false);
        if (callback?.success) getData();
    };

    const handleOpenModalDetailHoliday = (id: string, event: any) => {
        const { start } = event._instance.range;
        const isPastDate = validateIfDateIsBeforeToday(
            start.toISOString().split("T")[0]
        );
        const canEditPastDate = registrationsAllowedForPastDates.includes(
            GenericUtils.getUserAdp()
        );
        const readonly = isPastDate && !canEditPastDate;

        if (event._def.extendedProps.type === administrationAllowance) {
            setIdHoliday(id);
            setOpenModalDetailHoliday(false);
            setOpenModalDetailCustom({
                show: true,
                type: administrationAllowance,
                readonly,
            });
        } else if (event._def.extendedProps.type === activityManagementDate) {
            setIdHoliday(id);
            setOpenModalDetailHoliday(false);
            setOpenModalDetailCustom({
                show: true,
                type: activityManagementDate,
                readonly,
            });
        } else {
            setIdHoliday(id);
            setOpenModalDetailCustom({ ...initialCustomDetailModal, readonly });
            setOpenModalDetailHoliday(true);
        }
    };

    const handleCloseModalDetailHoliday = (callback?: ModalCallback) => {
        setOpenModalDetailHoliday(false);
        setOpenModalDetailCustom(initialCustomDetailModal);
        setOpenModalSaveHoliday(false);
        if (callback?.success) getData();
    };

    useEffect(() => {
        if (initialDate && finalDate) {
            getData();
        }
    }, [initialDate, finalDate, year]);

    const modalSaveHoliday = (
        <ModalSaveHoliday
            open={openModalSaveHoliday}
            onClose={handleCloseModalSaveHoliday}
            params={defaultHolidayParams}
            refresh={getData}
        />
    );

    const modalDetailHoliday = (
        <ModalDetailHoliday
            open={openModalDetailHoliday}
            onClose={handleCloseModalDetailHoliday}
            params={holidays}
            idHoliday={Number(idHoliday)}
            readonly={openModalDetailCustom.readonly}
        />
    );

    const modalDetailCustom = (
        <ModalDetailHoliday
            open={openModalDetailCustom.show}
            onClose={handleCloseModalDetailHoliday}
            params={holidays}
            idHoliday={Number(idHoliday)}
            type={openModalDetailCustom.type}
            readonly={openModalDetailCustom.readonly}
        />
    );

    const getMonthFull = (month: string) => {
        let exit = "";
        exit = month.length === 1 ? `0${month}` : month;
        return exit;
    };

    const holidaysView = holidays
        .sort((date1, date2) =>
            compareAsc(parseISO(date1.start), parseISO(date2.start))
        )
        .map((holiday: IHoliday) => (
            <div style={{ paddingLeft: 32 }} key={uuid()}>
                <strong>
                    {parseISO(holiday?.start).getDate()}/
                    {getMonthFull(`${parseISO(holiday?.start).getMonth() + 1}`)}
                </strong>{" "}
                - {holiday?.title}
            </div>
        ));

    return (
        <ThemeProvider theme={theme}>
            <StyledContainerHeader>
                <StyledTitle>Calendário da Agenda Docente</StyledTitle>
                <StyledSubTitle>
                    Navegue entre os meses e clique no dia escolhido para
                    cadastrar ou editar feriados, recessos, abonos e gestão de
                    atividades.
                </StyledSubTitle>
            </StyledContainerHeader>
            <FullCalendarApp
                data={holidays}
                setInitialDate={setInitialDate}
                setFinalDate={setFinalDate}
                addHoliday={handleOpenModalSaveHoliday}
                editHoliday={(a, b) => handleOpenModalDetailHoliday(a, b)}
            />
            <div
                style={{
                    marginTop: 48,
                    height: 105,
                    backgroundColor: "#F0F2F2",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                }}
            >
                <StyledSubTitle>Parâmetros incluídos</StyledSubTitle>
                {holidays.length > 0 ? (
                    <div style={{ display: "flex" }}>{holidaysView}</div>
                ) : (
                    <StyledLabel>Não há parâmetros cadastrados</StyledLabel>
                )}
                {openModalSaveHoliday ? modalSaveHoliday : null}
                {openModalDetailHoliday ? modalDetailHoliday : null}
                {openModalDetailCustom ? modalDetailCustom : null}
            </div>
            {isOpen && (
                <GlobalModal
                    type="error"
                    successMessage="Atenção."
                    text="Você não pode cadastrar um feriado, recessos ou gestão de atividades com data anterior a hoje."
                    onClose={closeModal}
                />
            )}
        </ThemeProvider>
    );
}

export default CalendarView;
